import { useCallback, useEffect, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { getClientAuth, getClientDB } from '@canvas/firebase-config/client';
import {
  projectCommentsPath,
  type ProjectComment,
  type ProjectCommentCreateInput,
} from '@canvas/shared-types';

function stripSlash(p: string) {
  return p.replace(/^\/+/, '');
}

function localKey(projectId: string) {
  return `craftara-comments:${projectId}`;
}

function readLocal(projectId: string): ProjectComment[] {
  try {
    const raw = localStorage.getItem(localKey(projectId));
    return raw ? (JSON.parse(raw) as ProjectComment[]) : [];
  } catch {
    return [];
  }
}

function writeLocal(projectId: string, comments: ProjectComment[]) {
  localStorage.setItem(localKey(projectId), JSON.stringify(comments));
}

export function useComments(projectId: string | undefined) {
  const [comments, setComments] = useState<ProjectComment[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) {
      setComments([]);
      return;
    }

    const auth = getClientAuth();
    const uid = auth.currentUser?.uid;
    if (!uid) {
      setComments(readLocal(projectId));
      return;
    }

    const db = getClientDB();
    const col = collection(db, stripSlash(projectCommentsPath(uid, projectId)));
    const unsub = onSnapshot(
      col,
      (snap) => {
        const rows = snap.docs.map((d) => d.data() as ProjectComment);
        rows.sort((a, b) => a.createdAt - b.createdAt);
        setComments(rows);
        writeLocal(projectId, rows);
        setError(null);
      },
      (err) => {
        console.warn('Comments sync failed, using local cache', err);
        setComments(readLocal(projectId));
        setError('Comments offline — saved locally');
      }
    );
    return () => unsub();
  }, [projectId]);

  const addComment = useCallback(
    async (input: ProjectCommentCreateInput) => {
      if (!projectId) return null;
      const auth = getClientAuth();
      const uid = auth.currentUser?.uid || 'local';
      const commentId = `cmt_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      const now = Date.now();
      const row: ProjectComment = {
        commentId,
        projectId,
        userId: uid,
        authorName:
          input.authorName ||
          auth.currentUser?.displayName ||
          auth.currentUser?.email?.split('@')[0] ||
          'You',
        body: input.body.trim(),
        x: input.x,
        y: input.y,
        resolved: false,
        createdAt: now,
        updatedAt: now,
      };

      if (!auth.currentUser) {
        const next = [...readLocal(projectId), row];
        writeLocal(projectId, next);
        setComments(next);
        return row;
      }

      const db = getClientDB();
      await setDoc(
        doc(db, stripSlash(projectCommentsPath(uid, projectId)), commentId),
        row
      );
      return row;
    },
    [projectId]
  );

  const resolveComment = useCallback(
    async (commentId: string, resolved = true) => {
      if (!projectId) return;
      const auth = getClientAuth();
      const uid = auth.currentUser?.uid;

      if (!uid) {
        const next = readLocal(projectId).map((c) =>
          c.commentId === commentId
            ? { ...c, resolved, updatedAt: Date.now() }
            : c
        );
        writeLocal(projectId, next);
        setComments(next);
        return;
      }

      const db = getClientDB();
      await updateDoc(
        doc(db, stripSlash(projectCommentsPath(uid, projectId)), commentId),
        { resolved, updatedAt: Date.now() }
      );
    },
    [projectId]
  );

  const deleteComment = useCallback(
    async (commentId: string) => {
      if (!projectId) return;
      const auth = getClientAuth();
      const uid = auth.currentUser?.uid;

      if (!uid) {
        const next = readLocal(projectId).filter((c) => c.commentId !== commentId);
        writeLocal(projectId, next);
        setComments(next);
        return;
      }

      const db = getClientDB();
      await deleteDoc(
        doc(db, stripSlash(projectCommentsPath(uid, projectId)), commentId)
      );
    },
    [projectId]
  );

  return { comments, error, addComment, resolveComment, deleteComment };
}
